// client/src/components/faculty/FacultyList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import facultyService from '../../services/facultyService';

const FacultyList = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facultyToDelete, setFacultyToDelete] = useState(null);

  // Current language
  const lang = i18n.language;

  // Function to load faculties
  const loadFaculties = async () => {
    try {
      setLoading(true);
      const response = await facultyService.getFaculties();
      setFaculties(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingFaculties'));
    } finally {
      setLoading(false);
    }
  };

  // Load faculties on component mount
  useEffect(() => {
    loadFaculties();
  }, []);

  // Handle faculty deletion
  const handleDeleteClick = (faculty) => {
    setFacultyToDelete(faculty);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await facultyService.deleteFaculty(facultyToDelete.id);
      setSuccess(t('facultyDeletedSuccess'));
      loadFaculties();
    } catch (err) {
      setError(t('errorDeletingFaculty'));
    } finally {
      setDeleteDialogOpen(false);
      setFacultyToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFacultyToDelete(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('faculties')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Faculty button - only for admins */}
      {isAdmin() && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/faculties/create"
          >
            {t('addFaculty')}
          </Button>
        </Box>
      )}

      {/* Faculties Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : faculties.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noFacultiesFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                {isAdmin() && <TableCell align="center">{t('actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {faculties.map((faculty) => (
                <TableRow key={faculty.id}>
                  <TableCell>{faculty[`name_${lang}`]}</TableCell>
                  <TableCell>{faculty[`description_${lang}`]}</TableCell>
                  {isAdmin() && (
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        component={RouterLink}
                        to={`/faculties/edit/${faculty.id}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(faculty)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteFacultyConfirmation', { 
              faculty: facultyToDelete?.[`name_${lang}`] 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FacultyList;